import React, { Component } from "react";
import axios from "axios";
import { Store } from "react-notifications-component";
import { Zap } from "lucide-react";

import setAuthToken from "../../../utils/setAuthToken";

import {
  Drawer,
  FormField,
  Input,
  Textarea,
  Select,
  Switch,
  Button,
  Spinner,
  ArticleStatusBadge,
} from "../../../design-system";

/**
 * ArticleQuickEditDrawer — édition rapide d'un article sans charger la page complète.
 *
 * Props :
 *   isOpen         : boolean
 *   onClose        : () => void
 *   articleId      : string | null — l'article à éditer
 *   onSaved        : (article) => void — callback après save (pour refresh la liste)
 */
class ArticleQuickEditDrawer extends Component {
  state = {
    loading: false,
    saving: false,
    article: null,
    title: "",
    slug: "",
    excerpt: "",
    type: "VEILLE_TECHNOLOGIQUE",
    status: "DRAFT",
    featured: false,
  };

  componentDidUpdate(prevProps) {
    if (this.props.isOpen && this.props.articleId && this.props.articleId !== prevProps.articleId) {
      this.loadArticle();
    }
    if (this.props.isOpen && !prevProps.isOpen && this.props.articleId) {
      this.loadArticle();
    }
  }

  loadArticle = async () => {
    try {
      this.setState({ loading: true });
      const token = localStorage.getItem("jwtToken");
      if (token) setAuthToken(token);
      const response = await axios.get(`/api/articles/id/${this.props.articleId}`);
      const a = response.data.article;
      this.setState({
        loading: false,
        article: a,
        title: a.title || "",
        slug: a.slug || "",
        excerpt: a.excerpt || "",
        type: a.type || "VEILLE_TECHNOLOGIQUE",
        status: a.status || "DRAFT",
        featured: a.featured || false,
      });
    } catch (error) {
      console.error("Error loading article:", error);
      this.setState({ loading: false });
      Store.addNotification({
        title: "Erreur",
        message: error.response?.data?.message || "Impossible de charger l'article",
        type: "danger", insert: "top", container: "top-right", dismiss: { duration: 3000 },
      });
      this.props.onClose?.();
    }
  };

  handleSubmit = async (e) => {
    if (e) e.preventDefault();
    const { article } = this.state;
    if (!article) return;
    try {
      this.setState({ saving: true });
      const token = localStorage.getItem("jwtToken");
      if (token) setAuthToken(token);
      const patch = {
        title: this.state.title,
        slug: this.state.slug,
        excerpt: this.state.excerpt,
        type: this.state.type,
        featured: this.state.featured,
      };
      if (this.state.status !== article.status) {
        patch.status = this.state.status;
      }
      const response = await axios.put(`/api/articles/${article.id}`, patch);
      const saved = response.data.article || { ...article, ...patch };
      Store.addNotification({
        title: "Article mis à jour",
        message: `« ${saved.title} » a été enregistré.`,
        type: "success", insert: "top", container: "top-right", dismiss: { duration: 2500 },
      });
      this.setState({ saving: false });
      this.props.onSaved?.(saved);
      this.props.onClose?.();
    } catch (error) {
      console.error("Error saving article:", error);
      this.setState({ saving: false });
      Store.addNotification({
        title: "Erreur",
        message: error.response?.data?.message || "Erreur lors de la sauvegarde",
        type: "danger", insert: "top", container: "top-right", dismiss: { duration: 5000 },
      });
    }
  };

  render() {
    const { isOpen, onClose } = this.props;
    const { loading, saving, article } = this.state;

    return (
      <Drawer
        isOpen={isOpen}
        onClose={onClose}
        side="right"
        title={
          <span className="inline-flex items-center gap-2">
            <Zap className="w-4 h-4 text-warn" strokeWidth={1.8} />
            Édition rapide
          </span>
        }
        description={article ? "Modifie les champs essentiels sans recharger la page." : "Chargement…"}
        footer={
          <>
            <Button variant="secondary" onClick={onClose} isDisabled={saving}>Annuler</Button>
            <Button onClick={this.handleSubmit} isLoading={saving} isDisabled={loading || !article}>
              Enregistrer
            </Button>
          </>
        }
      >
        {loading ? (
          <div className="flex items-center justify-center py-16 text-mute"><Spinner size="lg" /></div>
        ) : !article ? null : (
          <form onSubmit={this.handleSubmit} className="space-y-4">
            <div className="flex items-center gap-2 mb-1">
              <span className="text-[10.5px] font-mono uppercase tracking-wider text-mute">
                {article.id?.slice(0, 8)}
              </span>
              <ArticleStatusBadge status={this.state.status} size="sm" />
              {this.state.featured && (
                <span className="text-[10.5px] font-mono uppercase tracking-wider text-warn">★ à la une</span>
              )}
            </div>
            <FormField label="Titre" required>
              <Input
                value={this.state.title}
                onChange={(e) => this.setState({ title: e.target.value })}
                required
                autoFocus
              />
            </FormField>
            <FormField label="Slug" helper="URL (a-z, 0-9, -).">
              <Input
                value={this.state.slug}
                onChange={(e) => this.setState({ slug: e.target.value })}
                pattern="[a-z0-9-]+"
              />
            </FormField>
            <FormField label="Extrait">
              <Textarea
                rows={3}
                value={this.state.excerpt}
                onChange={(e) => this.setState({ excerpt: e.target.value })}
              />
            </FormField>
            <div className="grid grid-cols-2 gap-3">
              <FormField label="Statut">
                <Select
                  value={this.state.status}
                  onChange={(e) => this.setState({ status: e.target.value })}
                  options={[
                    { value: "DRAFT",     label: "Brouillon" },
                    ...(article.status === "SCHEDULED" ? [{ value: "SCHEDULED", label: "Programmé" }] : []),
                    { value: "PUBLISHED", label: "Publié" },
                    { value: "ARCHIVED",  label: "Archivé" },
                  ]}
                />
              </FormField>
              <FormField label="Type">
                <Select
                  value={this.state.type}
                  onChange={(e) => this.setState({ type: e.target.value })}
                  options={[
                    { value: "VEILLE_TECHNOLOGIQUE", label: "Veille technologique" },
                    { value: "VEILLE_JURIDIQUE",     label: "Veille juridique" },
                    { value: "STAGE",                label: "Stage" },
                    { value: "PPE",                  label: "PPE" },
                    { value: "PP",                   label: "PP" },
                    { value: "OTHER",                label: "Autre" },
                  ]}
                />
              </FormField>
            </div>
            <Switch
              checked={this.state.featured}
              onChange={(e) => this.setState({ featured: e.target.checked })}
              label="Mettre en avant"
              description="Apparaîtra sur la page d'accueil du portfolio."
            />
            <p className="text-xs text-mute pt-2 border-t border-border">
              Pour modifier le contenu, l'image ou les métadonnées SEO,{" "}
              <a
                href={`/dashboard/articles/${article.id}`}
                className="text-text font-medium hover:underline underline-offset-2"
              >
                ouvre la page d'édition complète
              </a>.
            </p>
          </form>
        )}
      </Drawer>
    );
  }
}

export default ArticleQuickEditDrawer;
